import { apiFetch } from '@/lib/api/http';

export type NotificationCategory = 'event' | 'survey' | 'message' | 'success' | 'score' | 'system';
export type ScheduleStatus = 'scheduled' | 'sent' | 'skipped' | 'cancelled';

export const SCHEDULE_STATUS_LABELS: Record<ScheduleStatus, string> = {
  scheduled: '配信予定',
  sent: '送信済み',
  skipped: 'スキップ',
  cancelled: 'キャンセル',
};

export interface AppNotification {
  id: string;
  category: NotificationCategory;
  title: string;
  body: string;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

export interface NotificationPage {
  items: AppNotification[];
  total: number;
  page: number;
  per_page: number;
}

export interface TriggerDef {
  base: string;
  offset?: string;
  at?: string | null;
  day?: number | null;
  weekday?: string | null;
  business_day_shift?: 'none' | 'before' | 'after';
}

export interface RuleDef {
  key: string;
  audience: string;
  template: string;
  trigger: TriggerDef;
  condition?: string | null;
  channels_by_pref?: boolean;
}

export interface NotificationPreset {
  id: string;
  code: string;
  name: string;
  definition: { rules: RuleDef[] };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationTemplate {
  id: string;
  code: string;
  name: string;
  category: NotificationCategory;
  event_type_id: string | null;
  event_type_name: string | null;
  subject: string;
  body: string;
  variables: string[];
  sender_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationSchedule {
  id: string;
  event_id: string | null;
  event_survey_id: string | null;
  rule_key: string;
  audience_key: string;
  occurrence_key: string | null;
  scheduled_for: string;
  condition: string | null;
  status: ScheduleStatus;
  sent_at: string | null;
  note: string | null;
  template_code: string | null;
  template_name: string | null;
  manual_subject: string | null;
  is_manual: boolean;
  time_overridden?: boolean;
  /** この回だけ足したメールの添付名（D-355） */
  attachment_name?: string | null;
  /** カレンダー起点の配信の周期（「毎月10日」「毎週金曜」）。イベント紐づきは null（D-311） */
  cadence_label?: string | null;
  /** 手入力通知の宛先絞り込み要約（D-358） */
  audience_filter_summary?: string | null;
}

/** 事務局が手入力で作る通知の宛先（旧一斉配信の統合先 / D-138） */
export type ManualAudienceKey = 'all' | 'success' | 'directors' | 'all_students';

export const MANUAL_AUDIENCE_LABELS: Record<ManualAudienceKey, string> = {
  all: '全員',
  all_students: '受講生全員',
  directors: '理事・講師',
  success: 'サクセス担当',
};

export type ManualUserActiveFilter = 'active' | 'all' | 'inactive';

export type ManualMemberStatusFilter = 'active' | 'graduated' | 'withdrawn' | 'expelled' | 'all';

export interface ManualNotificationCreateInput {
  audience_key: ManualAudienceKey;
  subject: string;
  body: string;
  sender_name?: string | null;
  scheduled_for?: string | null;
  attachment_key?: string | null;
  attachment_name?: string | null;
  attachment_size?: number | null;
  cohort_id?: string | null;
  course_id?: string | null;
  member_status?: ManualMemberStatusFilter | null;
  user_active?: ManualUserActiveFilter;
}

export interface SchedulePreview {
  schedule_id: string;
  scheduled_for: string;
  condition: string | null;
  condition_met: boolean | null;
  recipients: { user_id: string; display_name: string | null; email: string }[];
  subject: string;
  body: string;
  sender_name: string | null;
  attachment_name?: string | null;
  audience_filter_summary?: string | null;
}

// --- アプリ内通知（本人） ----------------------------------------------------

export function fetchNotifications(page = 1, unreadOnly = false): Promise<NotificationPage> {
  return apiFetch<NotificationPage>(
    `/notifications?page=${page}&unread_only=${unreadOnly ? 'true' : 'false'}`,
  );
}

export function fetchUnreadCount(): Promise<{ unread: number }> {
  return apiFetch<{ unread: number }>('/notifications/unread-count');
}

export function markNotificationRead(notificationId: string): Promise<AppNotification> {
  return apiFetch<AppNotification>(`/notifications/${notificationId}/read`, { method: 'PATCH' });
}

// --- 事務局: 配信予定 --------------------------------------------------------

export function fetchSchedules(params: {
  eventId?: string;
  status?: ScheduleStatus;
  page?: number;
  perPage?: number;
}): Promise<{ items: NotificationSchedule[]; total: number; page: number; per_page: number }> {
  const query = new URLSearchParams();
  if (params.eventId) query.set('event_id', params.eventId);
  if (params.status) query.set('status', params.status);
  query.set('page', String(params.page ?? 1));
  query.set('per_page', String(params.perPage ?? 50));
  return apiFetch(`/notification-schedules?${query.toString()}`);
}

export function createManualSchedule(
  payload: ManualNotificationCreateInput,
): Promise<NotificationSchedule> {
  return apiFetch<NotificationSchedule>('/notification-schedules/manual', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export interface EventSchedulePreviewItem {
  rule_key: string;
  template_code: string;
  template_name: string | null;
  audience_key: string;
  trigger_base: string;
  offset: string;
  at: string | null;
  condition: string | null;
  scheduled_for: string | null;
  skip_reason: string | null;
}

export interface EventSchedulePreview {
  preset_name: string | null;
  items: EventSchedulePreviewItem[];
}

export function previewSchedulesFromType(payload: {
  type_id: string;
  starts_at?: string | null;
  ends_at?: string | null;
}): Promise<EventSchedulePreview> {
  return apiFetch<EventSchedulePreview>('/notification-schedules/preview-from-type', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export interface NotificationAttachment {
  key: string;
  filename: string;
  size: number;
  content_type: string;
}

export function uploadNotificationAttachment(file: File): Promise<NotificationAttachment> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<NotificationAttachment>('/notification-schedules/attachments', {
    method: 'POST',
    body: form,
  });
}

export function createEventExtraSchedule(payload: {
  event_id: string;
  audience_key: string;
  subject: string;
  body: string;
  sender_name?: string | null;
  scheduled_for: string;
  attachment_key?: string | null;
  attachment_name?: string | null;
  attachment_size?: number | null;
}): Promise<NotificationSchedule> {
  return apiFetch<NotificationSchedule>('/notification-schedules/event-extra', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function rescheduleSchedule(
  scheduleId: string,
  scheduledFor: string,
): Promise<NotificationSchedule> {
  return apiFetch<NotificationSchedule>(`/notification-schedules/${scheduleId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scheduled_for: scheduledFor }),
  });
}

export function cancelSchedule(scheduleId: string): Promise<NotificationSchedule> {
  return apiFetch<NotificationSchedule>(`/notification-schedules/${scheduleId}/cancel`, {
    method: 'POST',
  });
}

export function fetchSchedulePreview(scheduleId: string): Promise<SchedulePreview> {
  return apiFetch<SchedulePreview>(`/notification-schedules/${scheduleId}/preview`);
}

export function testSendSchedule(scheduleId: string): Promise<{ sent_to: string }> {
  return apiFetch<{ sent_to: string }>(`/notification-schedules/${scheduleId}/test-send`, {
    method: 'POST',
  });
}

export function setEventNotificationsSuppression(
  eventId: string,
  suppressed: boolean,
): Promise<{ suppressed: boolean }> {
  return apiFetch<{ suppressed: boolean }>(`/events/${eventId}/notifications-suppression`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ suppressed }),
  });
}

// --- 事務局: プリセット・テンプレート ----------------------------------------

export function fetchPresets(): Promise<NotificationPreset[]> {
  return apiFetch<NotificationPreset[]>('/notification-presets');
}

export function createPreset(payload: {
  code: string;
  name: string;
  definition?: { rules: RuleDef[] };
}): Promise<NotificationPreset> {
  return apiFetch<NotificationPreset>('/notification-presets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function updatePreset(
  presetId: string,
  payload: { name?: string; definition?: { rules: RuleDef[] } },
): Promise<NotificationPreset> {
  return apiFetch<NotificationPreset>(`/notification-presets/${presetId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function setPresetActive(presetId: string, isActive: boolean): Promise<NotificationPreset> {
  return apiFetch<NotificationPreset>(`/notification-presets/${presetId}/active`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_active: isActive }),
  });
}

export function fetchNotificationTemplates(): Promise<NotificationTemplate[]> {
  return apiFetch<NotificationTemplate[]>('/notification-templates');
}

export function updateNotificationTemplate(
  templateId: string,
  payload: {
    name?: string;
    subject?: string;
    body?: string;
    sender_name?: string | null;
    category?: NotificationCategory;
  },
): Promise<NotificationTemplate> {
  return apiFetch<NotificationTemplate>(`/notification-templates/${templateId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function createNotificationTemplate(payload: {
  code?: string;
  name: string;
  category: NotificationCategory;
  subject: string;
  body: string;
  variables?: string[];
  sender_name?: string | null;
}): Promise<NotificationTemplate> {
  return apiFetch<NotificationTemplate>('/notification-templates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function setNotificationTemplateActive(
  templateId: string,
  isActive: boolean,
): Promise<NotificationTemplate> {
  return apiFetch<NotificationTemplate>(`/notification-templates/${templateId}/active`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_active: isActive }),
  });
}

// --- Slack・Chatworkの宛先登録（2026-08-18 先方回答 / D-106） ---------------

export type ExternalChannel = 'slack' | 'chatwork';

export interface ChannelPrefState {
  channel: string;
  is_set: boolean;
  is_enabled: boolean;
  destination_hint: string | null;
}

export interface ChannelPrefs {
  slack: ChannelPrefState;
  chatwork: ChannelPrefState;
}

export interface ChannelPrefUpdateInput {
  channel: ExternalChannel;
  /** null は登録の削除 */
  destination: string | null;
  is_enabled?: boolean;
}

export function fetchMyChannelPrefs(): Promise<ChannelPrefs> {
  return apiFetch<ChannelPrefs>('/notifications/channel-prefs/me');
}

export function updateMyChannelPref(input: ChannelPrefUpdateInput): Promise<ChannelPrefs> {
  return apiFetch<ChannelPrefs>('/notifications/channel-prefs/me', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function fetchMemberChannelPrefs(memberId: string): Promise<ChannelPrefs> {
  return apiFetch<ChannelPrefs>(`/members/${memberId}/channel-prefs`);
}

export function updateMemberChannelPref(
  memberId: string,
  input: ChannelPrefUpdateInput,
): Promise<ChannelPrefs> {
  return apiFetch<ChannelPrefs>(`/members/${memberId}/channel-prefs`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}
